import React, { useEffect, useRef } from 'react';
import { useGoogleMaps } from '../../hooks/useGoogleMaps';

export interface ParsedAddress {
  fullAddress: string;
  street?: string;
  city?: string;
  stateCode?: string;
  stateName?: string;
  zipCode?: string;
  countryCode?: string;
  countryName?: string;
  latitude?: number;
  longitude?: number;
}

interface GooglePlacesAddressInputProps {
  value: string;
  onChange: (value: string) => void;
  onAddressSelect: (address: ParsedAddress) => void;
  placeholder?: string;
  className?: string;
  types?: string[];
  name?: string;
  id?: string;
  required?: boolean;
}

function parsePlace(place: any): ParsedAddress {
  const components = place?.address_components || [];
  const byType = (type: string) => components.find((c: any) => c.types?.includes(type));

  const streetNumber = byType('street_number')?.long_name || '';
  const route = byType('route')?.long_name || '';
  const locality =
    byType('locality')?.long_name ||
    byType('postal_town')?.long_name ||
    byType('administrative_area_level_2')?.long_name ||
    byType('administrative_area_level_3')?.long_name ||
    byType('sublocality_level_1')?.long_name ||
    byType('sublocality')?.long_name ||
    byType('neighborhood')?.long_name ||
    '';
  const stateComponent = byType('administrative_area_level_1') || byType('administrative_area_level_2');
  const stateCode = stateComponent?.short_name || '';
  const stateName = stateComponent?.long_name || '';
  const zipCode = [
    byType('postal_code')?.long_name,
    byType('postal_code_prefix')?.long_name,
    byType('postal_code_suffix')?.long_name,
  ]
    .filter(Boolean)
    .join('-') || '';
  const countryCode = byType('country')?.short_name || '';
  const countryName = byType('country')?.long_name || '';

  return {
    fullAddress: place?.formatted_address || '',
    street: `${streetNumber} ${route}`.trim() || undefined,
    city: locality || undefined,
    stateCode: stateCode || undefined,
    stateName: stateName || undefined,
    zipCode: zipCode || undefined,
    countryCode: countryCode || undefined,
    countryName: countryName || undefined,
    latitude: place?.geometry?.location?.lat?.(),
    longitude: place?.geometry?.location?.lng?.(),
  };
}

export function GooglePlacesAddressInput({
  value,
  onChange,
  onAddressSelect,
  placeholder = 'Search address',
  className = '',
  types,
  name,
  id,
  required,
}: GooglePlacesAddressInputProps) {
  const { loaded, hasApiKey } = useGoogleMaps(['places']);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const lastEmittedAddressRef = useRef<string>('');
  const typesKey = types?.length ? types.join('|') : 'address';

  useEffect(() => {
    if (!loaded || !inputRef.current || !window.google?.maps?.places) return;
    const inputEl = inputRef.current;
    const typesOption = types?.length ? types : ['address'];

    const ac = new window.google.maps.places.Autocomplete(inputEl, {
      fields: ['address_components', 'formatted_address', 'geometry'],
      types: typesOption,
    });

    const listener = ac.addListener('place_changed', () => {
      const place = ac.getPlace();
      const parsed = parsePlace(place);
      if (parsed.fullAddress) {
        onChange(parsed.fullAddress);
      }
      lastEmittedAddressRef.current = parsed.fullAddress || '';
      onAddressSelect(parsed);
    });

    return () => {
      if (listener && window.google?.maps?.event) {
        window.google.maps.event.removeListener(listener);
      }
      if (window.google?.maps?.event) {
        window.google.maps.event.clearInstanceListeners(ac);
      }
    };
  }, [loaded, onAddressSelect, onChange, typesKey]);

  const handleBlur = () => {
    if (!loaded || !hasApiKey || !window.google?.maps?.Geocoder) return;
    const v = String(value || '').trim();
    if (!v) return;
    if (lastEmittedAddressRef.current && lastEmittedAddressRef.current === v) return;

    try {
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ address: v }, (results: any[], status: string) => {
        if (status !== 'OK' || !results?.length) return;
        const best = results[0];
        const parsed = parsePlace(best);
        if (parsed.fullAddress) {
          lastEmittedAddressRef.current = parsed.fullAddress;
          onChange(parsed.fullAddress);
        }
        onAddressSelect(parsed);
      });
    } catch {}
  };

  return (
    <input
      ref={inputRef}
      type="text"
      id={id}
      name={name}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onBlur={handleBlur}
      placeholder={hasApiKey ? placeholder : 'Google Maps key not configured'}
      className={[className, 'max-w-full min-w-0 box-border'].filter(Boolean).join(' ')}
      style={{ maxWidth: '100%' }}
      disabled={!hasApiKey}
      required={required}
      aria-required={required || undefined}
    />
  );
}
